/**
 * Per-user key/value storage in Supabase (replaces localStorage for user data).
 * All reads/writes go to public.user_data so data syncs across devices.
 * localStorage fallback is only for missing table / network errors — not RLS denials (see shouldFallbackUserDataToLocalStorage).
 */
import { supabase } from '../supabase';
import { buildHospitalsTableOrClause } from './hospitalId';
import {
  logSupabaseError,
  shouldFallbackUserDataToLocalStorage,
} from './supabaseErrors';
import {
  PHI_AUTOSAVE_DATA_KEYS,
  PHI_NARRATIVE_DATA_KEYS,
  PhiBlockedError,
  PhiNeedsAcknowledgmentError,
  enforcePhiForDataKey,
  phiFindingsToMetadata,
  scanUnknownPayload,
} from './phiScanner';
import { logSecurityEvent } from './securityEvents';

const LS_PREFIX = 'ud_';
const LEGACY_MIRROR_OVERRIDE_KEY = 'impacts_disable_legacy_user_mirror';

/**
 * Personal workspace keys. These must NEVER be stored in hospital_data.
 */
export const USER_PRIVATE_DATA_KEYS = new Set<string>([
  'dashboard_resources',
]);

/**
 * Hospital-shared keys that must NEVER be mirrored into user_data.
 * A missing hospital row must not fall back to another user's personal copy
 * (that is how directory data leaked across hospitals).
 */
export const HOSPITAL_SCOPED_NO_USER_MIRROR_KEYS = new Set<string>([
  'dashboard_department_contacts',
]);

export function isUserPrivateDataKey(dataKey: string): boolean {
  return USER_PRIVATE_DATA_KEYS.has(dataKey);
}

export function isHospitalScopedNoMirrorKey(dataKey: string): boolean {
  return HOSPITAL_SCOPED_NO_USER_MIRROR_KEYS.has(dataKey);
}

function localStorageKey(userId: string, dataKey: string): string {
  return `${LS_PREFIX}${userId}_${dataKey}`;
}

export async function getUserData<T = unknown>(userId: string, dataKey: string): Promise<T | null> {
  if (!userId || !dataKey) return null;
  const { data, error } = await supabase
    .from('user_data')
    .select('value')
    .eq('user_id', userId)
    .eq('data_key', dataKey)
    .maybeSingle();
  if (!error) {
    return (data?.value as T) ?? null;
  }
  if (shouldFallbackUserDataToLocalStorage(error)) {
    try {
      const raw = localStorage.getItem(localStorageKey(userId, dataKey));
      if (raw !== null) return JSON.parse(raw) as T;
    } catch {
      // ignore
    }
  }
  return null;
}

function assertPhiSafeForDataKey(dataKey: string, value: unknown, userId?: string): void {
  if (!PHI_NARRATIVE_DATA_KEYS.has(dataKey)) return;
  const result = scanUnknownPayload(value);
  const autosave = PHI_AUTOSAVE_DATA_KEYS.has(dataKey);
  try {
    // Autosave paths: high only. Explicit saves: medium requires prior PhiGuard acknowledgment.
    enforcePhiForDataKey(dataKey, value, { forceAcknowledged: autosave });
  } catch (err) {
    if (err instanceof PhiBlockedError) {
      void logSecurityEvent('phi_input_blocked', {
        userId: userId || null,
        metadata: phiFindingsToMetadata(err.findings, err.surface),
      });
      throw err;
    }
    if (err instanceof PhiNeedsAcknowledgmentError) {
      void logSecurityEvent('phi_input_warned', {
        userId: userId || null,
        metadata: phiFindingsToMetadata(err.findings, err.surface),
      });
      throw err;
    }
    throw err;
  }
  if (autosave && result.maxSeverity === 'medium') {
    void logSecurityEvent('phi_input_warned', {
      userId: userId || null,
      metadata: phiFindingsToMetadata(result.findings, `data_key:${dataKey}`),
    });
  }
}

export async function setUserData(userId: string, dataKey: string, value: unknown): Promise<void> {
  if (!userId || !dataKey) return;
  assertPhiSafeForDataKey(dataKey, value, userId);
  const { error } = await supabase
    .from('user_data')
    .upsert(
      {
        user_id: userId,
        data_key: dataKey,
        value: value as any,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id,data_key' }
    );
  if (error) {
    logSupabaseError('userData set error', error);
    if (shouldFallbackUserDataToLocalStorage(error)) {
      try {
        localStorage.setItem(localStorageKey(userId, dataKey), JSON.stringify(value));
      } catch {
        // ignore
      }
    }
  }
}

const USER_DATA_BATCH = 120;
const HOSPITAL_DATA_BATCH = 120;
let hospitalDataTableMissing = false;

function isHospitalDataTableMissingError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null;
  const msg = String(e?.message || '').toLowerCase();
  return e?.code === 'PGRST205' && msg.includes('hospital_data');
}

function handleHospitalDataError(context: string, error: unknown): void {
  if (isHospitalDataTableMissingError(error)) {
    if (!hospitalDataTableMissing) {
      hospitalDataTableMissing = true;
      console.warn('[userData] hospital_data unavailable; falling back to legacy user_data reads where available.');
    }
    return;
  }
  logSupabaseError(context, error);
}

/** Batch-load one data_key for many users (e.g. PECC activities for admin snapshot). */
export async function batchGetUserDataForKey<T = unknown>(
  userIds: string[],
  dataKey: string
): Promise<Map<string, T | null>> {
  const out = new Map<string, T | null>();
  const unique = [...new Set(userIds.filter(Boolean))];
  unique.forEach((id) => out.set(id, null));
  if (!dataKey || unique.length === 0) return out;

  for (let i = 0; i < unique.length; i += USER_DATA_BATCH) {
    const part = unique.slice(i, i + USER_DATA_BATCH);
    const { data, error } = await supabase
      .from('user_data')
      .select('user_id, value')
      .eq('data_key', dataKey)
      .in('user_id', part);
    if (error) {
      logSupabaseError(`batchGetUserDataForKey(${dataKey})`, error);
      continue;
    }
    (data || []).forEach((row: { user_id: string; value: unknown }) => {
      out.set(row.user_id, row.value as T);
    });
  }
  return out;
}

const SITE_REF_MAP_CHUNK = 60;

/**
 * Map hospitals.id or hospitals.facility_id string refs → canonical hospitals.id
 * (for hospital_data keys and checklist joins).
 */
export async function mapSiteRefsToHospitalRowIds(refs: string[]): Promise<Map<string, string>> {
  const map = new Map<string, string>();
  const unique = [...new Set(refs.map((r) => String(r || '').trim()).filter(Boolean))];
  if (!unique.length) return map;
  for (let i = 0; i < unique.length; i += SITE_REF_MAP_CHUNK) {
    const part = unique.slice(i, i + SITE_REF_MAP_CHUNK);
    const orClause = buildHospitalsTableOrClause(part);
    if (!orClause || orClause.includes('__no_match__')) continue;
    const { data, error } = await supabase.from('hospitals').select('id, facility_id').or(orClause);
    if (error) {
      logSupabaseError('mapSiteRefsToHospitalRowIds', error);
      continue;
    }
    (data || []).forEach((h: { id: string; facility_id: string | null }) => {
      map.set(String(h.id), String(h.id));
      if (h.facility_id != null && String(h.facility_id).trim()) {
        map.set(String(h.facility_id).trim(), String(h.id));
      }
    });
  }
  return map;
}

/** Resolve facility_id / id-like site references to canonical hospitals.id (UUID text). */
export async function resolveHospitalUuid(siteRef: string): Promise<string | null> {
  const ref = String(siteRef || '').trim();
  if (!ref) return null;
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(ref);

  if (isUuid) {
    const { data, error } = await supabase.from('hospitals').select('id').eq('id', ref).maybeSingle();
    if (!error && data?.id) return String(data.id);
  }

  const { data, error } = await supabase.from('hospitals').select('id, facility_id').eq('facility_id', ref).maybeSingle();
  if (error) {
    logSupabaseError(`resolveHospitalUuid(${ref})`, error);
    return null;
  }
  return data?.id ? String(data.id) : null;
}

/** Hospital-scoped key/value storage for PECC continuity across user turnover. */
export async function getHospitalData<T = unknown>(hospitalId: string, dataKey: string): Promise<T | null> {
  if (!hospitalId || !dataKey) return null;
  if (isUserPrivateDataKey(dataKey)) return null;
  if (hospitalDataTableMissing) return null;
  const { data, error } = await supabase
    .from('hospital_data')
    .select('value')
    .eq('hospital_id', hospitalId)
    .eq('data_key', dataKey)
    .maybeSingle();
  if (!error) return (data?.value as T) ?? null;
  handleHospitalDataError(`getHospitalData(${dataKey})`, error);
  return null;
}

/** Upsert hospital-scoped JSON value, preserving actor attribution server-side via auth.uid(). */
export async function setHospitalData(hospitalId: string, dataKey: string, value: unknown): Promise<void> {
  if (!hospitalId || !dataKey) return;
  if (isUserPrivateDataKey(dataKey)) {
    console.error(`[userData] refused hospital_data write for private key "${dataKey}"`);
    return;
  }
  if (hospitalDataTableMissing) return;
  assertPhiSafeForDataKey(dataKey, value);
  const { error } = await supabase
    .from('hospital_data')
    .upsert(
      {
        hospital_id: hospitalId,
        data_key: dataKey,
        value: value as any,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'hospital_id,data_key' }
    );
  if (error) handleHospitalDataError(`setHospitalData(${dataKey})`, error);
}

/**
 * Insert default JSON only if no row exists for (hospital_id, data_key).
 * Avoids wiping existing hospital continuity when a new PECC registers at a site that already has data.
 */
export async function ensureHospitalDataPlaceholder(
  hospitalId: string,
  dataKey: string,
  defaultValue: unknown = []
): Promise<void> {
  if (!hospitalId || !dataKey) return;
  if (isUserPrivateDataKey(dataKey)) return;
  if (hospitalDataTableMissing) return;
  const { data, error } = await supabase
    .from('hospital_data')
    .select('hospital_id')
    .eq('hospital_id', hospitalId)
    .eq('data_key', dataKey)
    .maybeSingle();
  if (error) {
    handleHospitalDataError(`ensureHospitalDataPlaceholder(${dataKey})`, error);
    return;
  }
  if (data) return;
  await setHospitalData(hospitalId, dataKey, defaultValue);
}

/**
 * Feature flag for the final cutover:
 * - default: mirror writes to both hospital_data and user_data
 * - cutover on: set REACT_APP_DISABLE_LEGACY_USER_MIRROR=true (or localStorage override key)
 */
export function shouldMirrorLegacyUserData(): boolean {
  try {
    const override = localStorage.getItem(LEGACY_MIRROR_OVERRIDE_KEY);
    if (override === 'true') return false;
    if (override === 'false') return true;
  } catch {
    // ignore localStorage unavailability
  }
  return process.env.REACT_APP_DISABLE_LEGACY_USER_MIRROR !== 'true';
}

/**
 * Read a continuity key: hospital_data first, then optional legacy user_data (same flag as writes).
 * After REACT_APP_DISABLE_LEGACY_USER_MIRROR=true, only hospital rows are used when hospitalId is set.
 */
export async function getContinuityData<T = unknown>(
  hospitalId: string | null | undefined,
  userId: string | null | undefined,
  dataKey: string
): Promise<T | null> {
  if (!dataKey) return null;
  if (isUserPrivateDataKey(dataKey)) {
    return userId ? getUserData<T>(userId, dataKey) : null;
  }
  if (isHospitalScopedNoMirrorKey(dataKey)) {
    return hospitalId ? getHospitalData<T>(hospitalId, dataKey) : null;
  }
  if (hospitalId) {
    const fromHospital = await getHospitalData<T>(hospitalId, dataKey);
    if (fromHospital != null) return fromHospital;
    if (shouldMirrorLegacyUserData() && userId) return getUserData<T>(userId, dataKey);
    return null;
  }
  if (userId) return getUserData<T>(userId, dataKey);
  return null;
}

/** Write continuity keys to hospital_data and (optionally) legacy user_data mirror. */
export async function writeContinuityData(
  hospitalId: string | null | undefined,
  userId: string | null | undefined,
  dataKey: string,
  value: unknown
): Promise<void> {
  if (!dataKey) return;
  if (isUserPrivateDataKey(dataKey)) {
    if (userId) await setUserData(userId, dataKey, value);
    return;
  }
  if (isHospitalScopedNoMirrorKey(dataKey)) {
    if (hospitalId) await setHospitalData(hospitalId, dataKey, value);
    return;
  }
  if (hospitalId) {
    if (shouldMirrorLegacyUserData() && userId) {
      await Promise.all([
        setHospitalData(hospitalId, dataKey, value),
        setUserData(userId, dataKey, value),
      ]);
      return;
    }
    await setHospitalData(hospitalId, dataKey, value);
    return;
  }
  if (userId) await setUserData(userId, dataKey, value);
}

/** Batch-load one key for many hospitals (e.g. hospital-owned PECC activities rollups). */
export async function batchGetHospitalDataForKey<T = unknown>(
  hospitalIds: string[],
  dataKey: string
): Promise<Map<string, T | null>> {
  const out = new Map<string, T | null>();
  const unique = [...new Set(hospitalIds.filter(Boolean))];
  unique.forEach((id) => out.set(id, null));
  if (!dataKey || unique.length === 0) return out;
  if (hospitalDataTableMissing) return out;

  for (let i = 0; i < unique.length; i += HOSPITAL_DATA_BATCH) {
    const part = unique.slice(i, i + HOSPITAL_DATA_BATCH);
    const { data, error } = await supabase
      .from('hospital_data')
      .select('hospital_id, value')
      .eq('data_key', dataKey)
      .in('hospital_id', part);
    if (error) {
      handleHospitalDataError(`batchGetHospitalDataForKey(${dataKey})`, error);
      continue;
    }
    (data || []).forEach((row: { hospital_id: string; value: unknown }) => {
      out.set(row.hospital_id, row.value as T);
    });
  }
  return out;
}

/** Migrate one key from localStorage to Supabase (call once on load if Supabase returns null). */
export async function migrateFromLocalStorage(
  userId: string,
  dataKey: string,
  lsKey: string,
  setFn: (value: unknown) => void
): Promise<void> {
  try {
    const raw = localStorage.getItem(lsKey);
    if (raw === null) return;
    const value = JSON.parse(raw);
    await setUserData(userId, dataKey, value);
    setFn(value);
    localStorage.removeItem(lsKey);
  } catch {
    // ignore
  }
}
